"use client";

import { useEffect, useRef, useState } from "react";
import { replacePlaceholders, scrollTo } from "@repo/shared-utils/common-utils";
import { StickyHeaderProps } from "./AMCStickyHeader.interface";
import AmcHideOnLogin from "../AMCHideOnLogin/AmcHideOnLogin";
import { recordAmcPdpCtaMetric } from "../../monitoring/amcPdpMonitoringHelpers";

/** Strips leading # from navigation IDs for DOM queries */
const sanitizeId = (id?: string): string => {
  if (!id) return "";
  return id.startsWith("#") ? id.substring(1) : id;
};

const getMostVisibleEntry = (entries: IntersectionObserverEntry[]) => {
  const intersectingEntries = entries.filter((entry) => entry?.isIntersecting);

  if (intersectingEntries.length === 0) return null;

  return intersectingEntries.reduce(
    (prev, current) =>
      current?.intersectionRatio > (prev?.intersectionRatio ?? 0) ? current : prev,
    intersectingEntries[0]
  );
};

const shouldBeSticky = () => window?.scrollY > 100;

const toggleStickyClasses = (isSticky: boolean) => {
  const wrapper = document.querySelector(".content-wrapper") as HTMLElement | null;
  const leftSection = document.querySelector(".left-section") as HTMLElement | null;

  if (!wrapper && !leftSection) return;

  if (isSticky) {
    wrapper?.classList.add("relative", "z-5");
    leftSection?.classList.add("mt-6");
  } else {
    wrapper?.classList.remove("relative", "z-5");
    leftSection?.classList.remove("mt-6");
  }
};

// extract scroll calculation (same math)
const getScrollPosition = (section: HTMLElement) => {
  const stickyHeaderElement = document.querySelector('[class*="fixed"]') as HTMLElement;

  const stickyHeaderHeight = stickyHeaderElement ? stickyHeaderElement.offsetHeight : 0;

  const additionalOffset = window.innerWidth < 768 ? 60 : 80;

  const totalOffset = stickyHeaderHeight + additionalOffset;

  const elementPosition = section.getBoundingClientRect().top + window.scrollY;

  return Math.max(0, elementPosition - totalOffset);
};

const AMCStickyHeader = ({ version, stickyHeaderData, amcDetails }: StickyHeaderProps) => {
  const [activeTab, setActiveTab] = useState("performance");
  const [isSticky, setIsSticky] = useState(false);

  const isClickingRef = useRef(false);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsSticky(shouldBeSticky());
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window?.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const observerCallback: IntersectionObserverCallback = (entries) => {
      if (isClickingRef.current) return;

      const mostVisible = getMostVisibleEntry(entries);
      if (!mostVisible) return;

      const id = (mostVisible.target as HTMLElement).id || "performance";

      if (id !== activeTab) {
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

        debounceTimerRef.current = setTimeout(() => {
          setActiveTab(id);

          const activeButton = document.querySelector<HTMLButtonElement>(`button[data-id="${id}"]`);

          if (activeButton) {
            activeButton.scrollIntoView({
              behavior: "smooth",
              inline: "center",
              block: "nearest",
            });
          }
        }, 300);
      }
    };

    const observer = new IntersectionObserver(observerCallback, {
      threshold: 0.1,
      rootMargin: "-140px 0px -40% 0px",
    });

    if (stickyHeaderData) {
      Object.values(stickyHeaderData)?.forEach((item) => {
        if (item?.navigationId) {
          const sectionId = sanitizeId(item.navigationId);
          const section = document.getElementById(sectionId);
          if (section) {
            observer.observe(section);
          }
        }
      });
    }

    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      observer.disconnect();
    };
  }, [stickyHeaderData, activeTab]);

  useEffect(() => {
    toggleStickyClasses(isSticky);
  }, [isSticky]);

  const handleTabClick = (id: string, tabLabel?: string) => {
    const sectionId = sanitizeId(id);
    const section = document.getElementById(sectionId);
    if (section) {
      const targetPosition = getScrollPosition(section);

      isClickingRef.current = true;
      setActiveTab(sectionId);

      window.scrollTo({
        top: targetPosition,
        behavior: "smooth",
      });

      setTimeout(() => {
        isClickingRef.current = false;
      }, 800);

      setTimeout(() => {
        const sectionRect = section.getBoundingClientRect();
        const buffer = 10;

        const stickyHeaderElement = document.querySelector('[class*="fixed"]') as HTMLElement;

        const stickyHeaderHeight = stickyHeaderElement ? stickyHeaderElement.offsetHeight : 0;

        const additionalOffset = window.innerWidth < 768 ? 60 : 80;
        const totalOffset = stickyHeaderHeight + additionalOffset;

        if (sectionRect.top < totalOffset - buffer) {
          const adjustment = totalOffset - sectionRect.top + buffer;

          window.scrollTo({
            top: window.scrollY - adjustment,
            behavior: "smooth",
          });
        }
      }, 400);
    }

    const button = document.querySelector<HTMLButtonElement>(`button[data-id="${sectionId}"]`);
    if (button) {
      button.scrollIntoView({ behavior: "smooth", inline: "center" });
    }

    if (section) {
      recordAmcPdpCtaMetric("sticky_header_tab_scroll", {
        componentName: "AMCStickyHeader",
        ctaName: tabLabel,
        success: true,
        extra: { section_id: sectionId },
      });
    }
  };

  type StickyTabItem = {
    navigationId?: string;
    navigationTitleText?: string;
  };

  const renderStickyButton = (
    item: StickyTabItem,
    index: number,
    isActive: boolean,
    handleTabClick: (tab: string, tabLabel?: string) => void
  ) => {
    const sectionId = sanitizeId(item?.navigationId);
    const tabLabel = replacePlaceholders(
      item.navigationTitleText ?? "",
      amcDetails as unknown as Record<string, string>
    );

    return (
      <button
        key={`stickyLink-${index}`}
        data-id={sectionId}
        data-testid={`sticky-header-tab-${sectionId}`}
        data-monitor-click
        onClick={() => handleTabClick(item.navigationId ?? "", tabLabel)}
        className={`mx-4 cursor-pointer py-3 text-sm leading-[19px] whitespace-nowrap md:py-6 md:text-base ${
          isActive
            ? "text-link-primary after:bg-link-primary relative font-medium after:right-0 after:bottom-[0px] after:h-[1px] after:w-[100%] md:after:absolute md:after:content-['']"
            : "text-bfl-blue"
        }`}
      >
        {tabLabel}
      </button>
    );
  };

  return (
    <div
      className={`fixed left-0 z-5 ${
        version !== "in" ? "top-[75px] md:top-[75px]" : "top-[155px] md:top-[85px]"
      } w-full bg-white shadow-lg ${!isSticky ? "hidden" : ""}`}
      data-cmp="AMCStickyHeader"
      data-cmp-type="react"
      data-cmp-title="Sticky Header"
    >
      <nav className="flex-between-center">
        <div className="flex-between-center no-scrollbar w-[80%] overflow-scroll xl:overflow-hidden">
          {Object.values(stickyHeaderData ?? {}).map((item, index) => {
            const protectedTabs = ["YouAreHere", "RelatedLinks"];

            const sectionId = sanitizeId(item?.navigationId);
            const isProtectedTab = protectedTabs?.includes(sectionId);

            const isActive = activeTab === sectionId;

            return isProtectedTab ? (
              <AmcHideOnLogin key={item?.navigationTitleText}>
                {renderStickyButton(item, index, isActive, handleTabClick)}
              </AmcHideOnLogin>
            ) : (
              renderStickyButton(item, index, isActive, handleTabClick)
            );
          })}
        </div>
        <button
          data-testid="sticky-header-scroll-top"
          data-monitor-click
          onClick={() => scrollTo(0, "smooth")}
          className="cursor-pointer border-l border-gray-400 bg-orange-100 px-[10px] py-2 md:px-6 md:py-5"
        >
          <i className="icomoon text-sm before:content-['\e952']"></i>
        </button>
      </nav>
    </div>
  );
};

export default AMCStickyHeader;
